import { DailyCall } from "@daily-co/daily-js";
import { useToastController } from "@tamagui/toast";
import { useCallback, useContext, useEffect, useRef } from "react";

import {
  useCreateVideoVisitMeetingToken,
  useGetClinicStatusForPatient,
} from "@/api/generated";
import { LayoutContext } from "@/components";
import VideoCallContainer from "@/conversation/video/VideoCallContainer";
import {
  useCurrentConversationId,
  useIsConversationClosed,
  useNoriConversationId,
  useNoriConversationIdByCuraiId,
} from "@/hooks/conversation";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useSmsConsentNudge } from "@/hooks/useSmsConsentNudge";
import { captureException, logger, setTag } from "@/sentry";
import { VideoModes, useVideoStore } from "@/store";

export const useVideoPreview = (
  callObject: DailyCall | null,
  onDevices: (devices: MediaDeviceInfo[]) => void,
  onError: (error: Error) => void,
) => {
  const callbacksRef = useRef({ onDevices, onError });
  callbacksRef.current = { onDevices, onError };

  useEffect(() => {
    if (!callObject || callObject.isDestroyed()) return;

    let isPreviewClosed = false;
    const initPreview = async () => {
      try {
        await callObject.startCamera();
        if (isPreviewClosed) return;
        const { devices } = await callObject.enumerateDevices();
        if (isPreviewClosed) return;
        callbacksRef.current.onDevices(devices);
      } catch (error) {
        if (!isPreviewClosed) callbacksRef.current.onError(error as Error);
      }
    };
    void initPreview();

    return () => {
      // Stop pending initialization when camera access or device enumeration resolves.
      isPreviewClosed = true;
      // Release preview media unless it is transferring to the live call.
      if (useVideoStore.getState().videoMode === VideoModes.NONE) {
        useVideoStore.getState().setVideoCallConversationId(undefined);
        if (!callObject.isDestroyed()) {
          void callObject.leave().catch(captureException);
        }
      }
    };
  }, [callObject]);
};

export const useCreateCall = (callObject: DailyCall | null) => {
  const { data: clinicStatus } = useGetClinicStatusForPatient();
  const encounterId = clinicStatus?.conversation?.curai_encounter_id;
  const { mutate: createVideoVisitToken } = useCreateVideoVisitMeetingToken();
  const {
    setIsVideoVisitStarted,
    setVideoMode,
    setVideoCallConversationId,
    videoCallConversationId,
  } = useVideoStore();
  const { show } = useToastController();
  const currentConversationId = useCurrentConversationId();
  const router = usePropagatedRouter();
  const originatingNoriConversationId = useNoriConversationIdByCuraiId(
    videoCallConversationId,
  );
  const currentNoriConversationId = useNoriConversationId();
  const { triggerNudge: triggerSmsConsentNudge, shouldPromptSmsConsent } =
    useSmsConsentNudge(originatingNoriConversationId);

  const joinCall = useCallback(() => {
    if (!callObject || !encounterId) return;

    setTag("event.av_call", true);

    createVideoVisitToken(
      { data: { encounter_id: encounterId as string } },
      {
        onSuccess: async ({ room_name: roomName, url: baseUrl, token }) => {
          const url = `${baseUrl}/${roomName}`;
          try {
            await callObject.join({ token, url });
            if (!videoCallConversationId) {
              setVideoCallConversationId(currentConversationId);
            }
          } catch (err: any) {
            captureException(err);
            show("Failed to join video call. Please try again.", {
              duration: 5000,
            });
            callObject?.destroy();
            setVideoMode(VideoModes.NONE);
            setIsVideoVisitStarted(false);
          }
        },
        onError: (err: any) => {
          captureException(err);
          show("Failed to create video call. Please try again.", {
            duration: 5000,
          });
          callObject?.destroy();
          setVideoMode(VideoModes.NONE);
          setIsVideoVisitStarted(false);
        },
      },
    );
  }, [
    callObject,
    createVideoVisitToken,
    encounterId,
    setVideoMode,
    show,
    setIsVideoVisitStarted,
    setVideoCallConversationId,
    currentConversationId,
    videoCallConversationId,
  ]);

  const leaveCallImpl = useCallback(() => {
    if (!callObject) return;

    callObject
      .leave()
      .catch((err: any) => {
        captureException(err);
        show("Failed to leave video call. Please try again.", {
          duration: 5000,
        });
      })
      .finally(() => {
        if (shouldPromptSmsConsent && originatingNoriConversationId) {
          triggerSmsConsentNudge(originatingNoriConversationId, "video-visit");
        }
        if (currentNoriConversationId) {
          router.navigate(`/conversation/${currentNoriConversationId}`);
        }
        setVideoMode(VideoModes.NONE);
        setIsVideoVisitStarted(false);
        setVideoCallConversationId(undefined);
      });
  }, [
    callObject,
    setVideoMode,
    show,
    setIsVideoVisitStarted,
    setVideoCallConversationId,
    originatingNoriConversationId,
    currentNoriConversationId,
    router,
    triggerSmsConsentNudge,
    shouldPromptSmsConsent,
  ]);

  // `leaveCall` is consumed both as a dependency of the right-sidebar effect in
  // `useVideoCallManager` and as a prop on the active call UI. Its underlying
  // deps (sms-consent nudge state, derived nori conversation ids) settle
  // asynchronously after the video preview opens, which would otherwise churn
  // `leaveCall`'s identity. We keep a stable identity that always invokes the
  // latest implementation so the sidebar effect runs only on real `videoMode`
  // transitions — otherwise it re-fires while `videoMode` is still NONE and
  // clears the preview out from under the user (and remounts the call UI
  // mid-call once joined).
  const leaveCallRef = useRef(leaveCallImpl);
  leaveCallRef.current = leaveCallImpl;
  const leaveCall = useCallback(() => leaveCallRef.current(), []);

  return { callObject, joinCall, leaveCall };
};

/**
 * Manages video call lifecycle in the layout:
 * - Auto joins when videoMode changes from NONE to active
 * - Auto leaves when conversation ends
 * - Manages modal content for FULLSCREEN mode
 * - Returns data needed for rendering FLOATING mode
 */
export const useVideoCallManager = (callObject: DailyCall | null) => {
  const { videoMode, videoCallConversationId } = useVideoStore();
  const isVideoCallConversationClosed = useIsConversationClosed(
    videoCallConversationId,
  );
  const { leaveCall, joinCall } = useCreateCall(callObject);
  const { setModalContent } = useContext(LayoutContext);

  const prevVideoModeRef = useRef(videoMode);
  useEffect(() => {
    const prevMode = prevVideoModeRef.current;
    prevVideoModeRef.current = videoMode;

    if (
      prevMode === VideoModes.NONE &&
      videoMode !== VideoModes.NONE &&
      callObject
    ) {
      joinCall();
    }
  }, [videoMode, callObject, joinCall]);

  useEffect(() => {
    if (isVideoCallConversationClosed && videoMode !== VideoModes.NONE) {
      logger.info("Video call conversation ended, leaving video call", {
        category: "video",
      });
      leaveCall();
    }
  }, [isVideoCallConversationClosed, videoMode, leaveCall]);

  useEffect(() => {
    if (videoMode === VideoModes.FULLSCREEN) {
      setModalContent(() => () => (
        <VideoCallContainer mode={videoMode} leaveCall={leaveCall} />
      ));
    } else {
      setModalContent(null);
    }
  }, [videoMode, setModalContent, leaveCall]);

  return {
    videoMode,
    leaveCall,
  };
};
